import { useState, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { apiRequest } from '@/services/api'
import { validatePasswordStrength } from '@/utils/passwordValidation'
import Spinner from '@/components/Spinner'

/**
 * Reset password page component
 * Page for resetting password using token from email link
 */
export default function ResetPasswordPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')

  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [isValidating, setIsValidating] = useState(true)
  const validationAttemptedRef = useRef(false)

  // Verify token on mount
  useEffect(() => {
    if (!token) {
      navigate('/forgot-password', { replace: true })
      return
    }

    // Prevent multiple validation attempts
    if (validationAttemptedRef.current) {
      return
    }

    const verifyToken = async () => {
      validationAttemptedRef.current = true
      setIsValidating(true)

      try {
        const response = await apiRequest({
          method: 'GET',
          url: `/auth/verify-reset-token/${token}`,
        })

        if (!response.success) {
          setError(t('auth.resetLinkExpired'))
          setIsValidating(false)
          return
        }

        // Token is valid, allow form to be shown
        setIsValidating(false)
      } catch (err: any) {
        // Any error means token is invalid
        setError(t('auth.resetLinkExpired'))
        setIsValidating(false)
        setTimeout(() => {
          navigate('/forgot-password', { replace: true })
        }, 3000)
      }
    }

    verifyToken()
  }, [token, navigate, t])

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {}

    if (!password) {
      errors.password = t('errors.required')
    } else {
      const passwordValidation = validatePasswordStrength(password)
      if (!passwordValidation.valid) {
        errors.password = t('errors.passwordRequirements')
      }
    }

    if (password !== confirmPassword) {
      errors.confirmPassword = t('errors.passwordsDoNotMatch')
    }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!token) {
      setError(t('errors.invalidToken'))
      return
    }

    if (!validateForm()) {
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      const response = await apiRequest({
        method: 'POST',
        url: '/auth/reset-password',
        data: {
          token,
          password,
        },
      })

      if (response.success) {
        setSuccess(true)
        // Redirect to login after 2 seconds
        setTimeout(() => {
          navigate('/login', { replace: true })
        }, 2000)
      } else {
        // Show specific error if token is expired
        if (response.code === 'INVALID_OR_EXPIRED_TOKEN') {
          setError(t('auth.resetLinkExpired'))
          setTimeout(() => {
            navigate('/forgot-password', { replace: true })
          }, 3000)
        } else {
          setError(response.message || t('errors.resetPasswordFailed'))
        }
      }
    } catch (err: any) {
      setError(err.message || t('errors.resetPasswordFailed'))
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!token) {
    return null
  }

  // Show loading while validating token
  if (isValidating) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <Spinner />
          <p className="mt-4 text-slate-400 font-mono">{t('common.loading')}</p>
        </div>
      </div>
    )
  }

  // Show error if token is invalid/expired
  if (error) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4">
        <div className="relative w-full max-w-lg">
          {/* Glow effect */}
          <div className="absolute -inset-1 bg-linear-to-r from-red-500 via-purple-500 to-cyan-500 rounded-2xl opacity-20 blur-2xl" />

          {/* Card */}
          <div className="relative bg-slate-900 border border-slate-700/50 rounded-2xl shadow-2xl p-8 backdrop-blur-sm">
            <div className="text-center py-8">
              <div className="text-5xl mb-4">❌</div>
              <h2 className="text-2xl font-bold text-red-400 mb-2">
                {t('auth.resetLinkExpired')}
              </h2>
              <p className="text-slate-400 font-mono text-sm mb-6">
                {t('auth.resetLinkExpiredDescription')}
              </p>
              <Link
                to="/forgot-password"
                className="inline-block px-4 py-2.5 bg-linear-to-r from-red-600 to-red-500 hover:from-red-500 hover:to-red-400 text-white font-bold rounded-lg transition duration-200 text-sm uppercase tracking-wider"
              >
                {t('auth.requestNewResetLink')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (success) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4">
        <div className="relative w-full max-w-lg">
          {/* Glow effect */}
          <div className="absolute -inset-1 bg-linear-to-r from-green-500 via-cyan-500 to-purple-500 rounded-2xl opacity-20 blur-2xl" />

          {/* Card */}
          <div className="relative bg-slate-900 border border-slate-700/50 rounded-2xl shadow-2xl p-8 backdrop-blur-sm">
            <div className="text-center py-8">
              <div className="text-5xl mb-4">✅</div>
              <h2 className="text-2xl font-bold text-green-400 mb-2">
                {t('auth.passwordResetSuccess')}
              </h2>
              <p className="text-slate-400 font-mono text-sm mb-6">
                {t('auth.passwordResetSuccessDescription')}
              </p>
              <Link
                to="/login"
                className="inline-block px-4 py-2.5 bg-linear-to-r from-cyan-600 to-cyan-500 hover:from-cyan-500 hover:to-cyan-400 text-white font-bold rounded-lg transition duration-200 text-sm uppercase tracking-wider"
              >
                {t('auth.backToLogin')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center py-8 px-4">
      <div className="relative w-full max-w-lg">
        {/* Glow effect */}
        <div className="absolute -inset-1 bg-linear-to-r from-cyan-500 via-purple-500 to-red-500 rounded-2xl opacity-20 blur-2xl" />

        {/* Card */}
        <div className="relative bg-slate-900 border border-slate-700/50 rounded-2xl shadow-2xl p-8 backdrop-blur-sm">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-black bg-linear-to-r from-cyan-400 via-purple-400 to-red-500 bg-clip-text text-transparent">
              KaraoNow
            </h1>
            <h2 className="text-xl font-bold text-white mt-4">
              {t('auth.resetPassword')}
            </h2>
            <p className="mt-2 text-sm text-slate-400 font-mono">
              {t('auth.resetPasswordDescription')}
            </p>
          </div>

          <form className="space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="p-4 bg-red-900/30 border border-red-500/50 rounded-lg">
                <p className="text-sm font-mono text-red-300">
                  <span className="text-red-500">{'[ERROR]'}</span> {error}
                </p>
                {error === t('auth.resetLinkExpired') && (
                  <p className="text-xs font-mono text-red-300 mt-2">
                    {t('auth.resetLinkExpiredDescription')}
                  </p>
                )}
              </div>
            )}

            <div className="space-y-4">
              <p className="text-xs text-slate-400 font-mono">
                {t('auth.passwordRequirements')}
              </p>

              <div>
                <label className="block text-xs font-mono font-bold text-cyan-400 mb-2 uppercase tracking-wider">
                  {t('auth.password')}
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isSubmitting}
                  autoComplete="new-password"
                  className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 placeholder-slate-500 font-mono text-sm focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/30 transition disabled:opacity-50"
                  placeholder="••••••••"
                  required
                />
                {formErrors.password && (
                  <p className="text-xs text-red-400 font-mono mt-1">{formErrors.password}</p>
                )}
              </div>

              <div>
                <label className="block text-xs font-mono font-bold text-cyan-400 mb-2 uppercase tracking-wider">
                  {t('auth.confirmPassword')}
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={isSubmitting}
                  autoComplete="new-password"
                  className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 placeholder-slate-500 font-mono text-sm focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/30 transition disabled:opacity-50"
                  placeholder="••••••••"
                  required
                />
                {formErrors.confirmPassword && (
                  <p className="text-xs text-red-400 font-mono mt-1">{formErrors.confirmPassword}</p>
                )}
              </div>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full mt-6 px-4 py-3 bg-linear-to-r from-cyan-600 to-cyan-500 hover:from-cyan-500 hover:to-cyan-400 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold rounded-lg transition duration-200 text-sm uppercase tracking-wider"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="inline-block animate-spin">⟳</span>
                  {t('common.loading')}
                </span>
              ) : (
                t('auth.resetPasswordButton')
              )}
            </button>

            <div className="text-center">
              <p className="text-sm text-slate-400 font-mono">
                {t('auth.rememberPassword')}{' '}
                <Link to="/login" className="text-cyan-400 hover:text-cyan-300 font-bold">
                  {t('auth.login')}
                </Link>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

